import { type ReactNode } from 'react';
import toast from 'react-hot-toast/headless';
import { useMedia } from 'react-use';
import { Toast, ToastVariant } from '@hungryroot/components-toast';
import { FavoriteGroceries } from '@hungryroot/favorites';
import { CaretLeft } from '@hungryroot/icons';
import { IconButton, type IconButtonProps } from '@hungryroot/ramen';
import { useIdentify } from '@hungryroot/rest-hooks';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { ShareButtonContainer } from '../ShareButtonContainer/ShareButtonContainer';
import { type StackProps } from './PDPModal';

import styles from './PDPModalIcons.module.css';

const getIconButtonProps = (
  scrolled: boolean
): Pick<IconButtonProps, 'prominence' | 'style' | 'size'> => {
  return {
    prominence: scrolled ? 'tertiary' : 'primary',
    size: 'small',
    style: scrolled ? 'muted' : 'inverted',
  };
};

type ScrollProps = {
  scrolled: boolean;
};

type BackIconProps = {
  onClick: () => void;
} & ScrollProps;

const BackIcon = ({ scrolled, onClick }: BackIconProps) => (
  <IconButton
    ariaLabel="back"
    icon={<CaretLeft />}
    onClick={onClick}
    {...getIconButtonProps(scrolled)}
  />
);

type FavoriteIconProps = {
  onFavorite?: () => void;
  exclude?: boolean;
  product: { id: number; name: string };
} & ScrollProps;

const FavoriteIcon = ({
  exclude,
  product,
  scrolled,
  onFavorite,
}: FavoriteIconProps) => {
  const { customerData } = useIdentify();

  if (exclude) return null;

  return customerData?.id ? (
    <FavoriteGroceries
      customerId={customerData?.id}
      productId={product.id}
      productName={product.name}
      segment={{
        location: 'PDP',
      }}
      onFavorite={onFavorite}
      iconButtonVariant={getIconButtonProps(scrolled)}
      variant="PDP"
    />
  ) : null;
};

type ShareButtonProps = {
  onShareSuccess?: () => void;
  exclude?: boolean;
  product: {
    slug?: string;
    name: string;
  };
} & ScrollProps;

const ShareButton = ({
  exclude,
  product,
  scrolled,
  onShareSuccess,
}: ShareButtonProps) => {
  const { name, slug } = product;
  const isDesktop = useMedia(BreakpointQueryLarge);

  if (exclude) return null;

  return (
    <ShareButtonContainer
      title={name}
      text={name}
      {...getIconButtonProps(scrolled)}
      url={`${window.location.origin}/products/${slug}/`}
      onShareSuccess={() => {
        onShareSuccess?.();
        if (isDesktop) {
          toast(<Toast variant={ToastVariant.Success}>Link copied</Toast>);
        }
      }}
    />
  );
};

export type GetIconsArgs = StackProps & {
  product?: { id: number; name: string; slug?: string };
  exclusions?: ('favorite' | 'share')[];
  onShareSuccess?: () => void;
  onFavorite?: () => void;
} & ScrollProps;

export const Icons = (args: GetIconsArgs): ReactNode => {
  const { exclusions, product, scrolled } = args;

  if (!product) return null;

  return (
    <div className={styles['header-icons']}>
      {args.stacked && (
        <BackIcon onClick={args.onBackClick} scrolled={scrolled} />
      )}
      <ShareButton
        exclude={exclusions?.includes('share')}
        product={product}
        scrolled={scrolled}
        onShareSuccess={args.onShareSuccess}
      />
      <FavoriteIcon
        exclude={exclusions?.includes('favorite')}
        product={product}
        scrolled={scrolled}
        onFavorite={args.onFavorite}
      />
    </div>
  );
};
